import type {
  ColumnFiltersState,
  ColumnPinningState,
  ColumnSizingState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";
import * as React from "react";

import { prioritizeColumnIds, useColumnOrder } from "./useColumnOrder";

import { SELECTOR_ID } from "@/app/constants/global-constants";
import { Skeleton } from "@/components/ui/skeleton";
import type { ExtendedColumnDef } from "@/interfaces/globals";
import { cn } from "@/utils/core/generalUtils";

interface WpxTableColumnStateParams<TData, TValue> {
  columns: ExtendedColumnDef<TData, TValue>[];
  isColumnDraggable: boolean;
  isLoading: boolean;
  loaderClassName?: string;
  priorityColumnIds?: string[];
  title: string;
  activeTab?: string;
}

interface WpxTableColumnStateReturn<TData, TValue> {
  columnFilters: ColumnFiltersState;
  columnOrder: string[];
  columnPinning: ColumnPinningState;
  columnSizing: ColumnSizingState;
  columnVisibilityState: VisibilityState;
  defaultColumnOrder: string[];
  filteredColumns: ExtendedColumnDef<TData, TValue>[];
  resetColumnOrder: () => void;
  setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  setColumnOrder: React.Dispatch<React.SetStateAction<string[]>>;
  setColumnPinning: React.Dispatch<React.SetStateAction<ColumnPinningState>>;
  setColumnSizing: React.Dispatch<React.SetStateAction<ColumnSizingState>>;
  setColumnVisibility: React.Dispatch<React.SetStateAction<VisibilityState>>;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  sorting: SortingState;
  tableColumns: ExtendedColumnDef<TData, TValue>[];
}

const createTableId = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^\w]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

/**
 * Manages column-level state: filtering, sorting, pinning, sizing, visibility,
 * ordering, and the loading-skeleton column overlay.
 *
 * @param {WpxTableColumnStateParams<TData, TValue>} params - Column configuration inputs.
 * @returns {WpxTableColumnStateReturn<TData, TValue>} Column state and setters consumed by the table instance.
 */
export const useWpxTableColumnState = <TData, TValue>(
  params: WpxTableColumnStateParams<TData, TValue>,
): WpxTableColumnStateReturn<TData, TValue> => {
  const {
    columns,
    isColumnDraggable,
    isLoading,
    loaderClassName,
    priorityColumnIds = [],
    title,
    activeTab,
  } = params;

  const filteredColumns = React.useMemo(() => {
    return columns.filter((column) => !column.hideColumn);
  }, [columns]);

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );

  const initialPinning = React.useMemo(() => {
    return columns.reduce(
      (pinningState, column) => {
        if (!column.meta?.pin) {
          return pinningState;
        }

        const columnId =
          column.id ??
          (typeof column.accessorKey === "string" ? column.accessorKey : null);

        if (!columnId) {
          return pinningState;
        }

        if (column.meta.pinDirection === "left") {
          pinningState.left.push(columnId);
        }

        if (column.meta.pinDirection === "right") {
          pinningState.right.push(columnId);
        }

        return pinningState;
      },
      { left: [] as string[], right: [] as string[] },
    );
  }, [columns]);

  const [columnPinning, setColumnPinning] =
    React.useState<ColumnPinningState>(initialPinning);

  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>({});

  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});

  const defaultColumnOrder = React.useMemo((): string[] => {
    const columnIds = filteredColumns.map(
      (column) => column.id ?? String(column.accessorKey ?? ""),
    );

    return prioritizeColumnIds(columnIds, priorityColumnIds);
  }, [filteredColumns, priorityColumnIds]);

  const tableId = React.useMemo(() => {
    const baseTableId = createTableId(title);

    return activeTab ? `${baseTableId}__${activeTab}` : baseTableId;
  }, [title, activeTab]);

  const { columnOrder, resetColumnOrder, setColumnOrder } = useColumnOrder({
    tableId,
    defaultColumns: defaultColumnOrder,
    isColumnDraggable,
    priorityColumns: priorityColumnIds,
  });

  const columnVisibilityState = React.useMemo(() => {
    return {
      ...filteredColumns.reduce((visibilityState, column) => {
        if (column.id && column.enableHiding) {
          visibilityState[column.id] = !column.enableHiding;
        }

        return visibilityState;
      }, {} as VisibilityState),
      ...columnVisibility,
    };
  }, [columnVisibility, filteredColumns]);

  const tableColumns = React.useMemo(() => {
    if (!isLoading) {
      return filteredColumns;
    }

    return filteredColumns.map((column) => ({
      ...column,
      cell: (): JSX.Element => (
        <Skeleton
          className={cn(
            column.id === SELECTOR_ID && "w-8",
            "h-10 w-full",
            loaderClassName,
          )}
        />
      ),
    }));
  }, [filteredColumns, isLoading, loaderClassName]);

  return {
    columnFilters,
    columnOrder,
    columnPinning,
    columnSizing,
    columnVisibilityState,
    defaultColumnOrder,
    filteredColumns,
    resetColumnOrder,
    setColumnFilters,
    setColumnOrder,
    setColumnPinning,
    setColumnSizing,
    setColumnVisibility,
    setSorting,
    sorting,
    tableColumns,
  };
};
